import React from "react";
import { preventNegativeValueInDocs } from "_helpers/helpers";

function fmtMoney(n) {
  if (n === null || Number.isNaN(n)) return "";
  if (n === 0) {
    return (0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
  if (n < 0) return "";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function parseChildCount(raw) {
  const s = String(raw ?? "").trim();
  if (s === "") return null;
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** rate null = no deduction (—) */
const SCHED2_SECTIONS = [
  {
    key: "single",
    filingLabel: "Single",
    rows: [
      { agi: "Up to $20,000", rate: 3000 },
      { agi: "Over $20,000 Up to $30,000", rate: 2500 },
      { agi: "Over $30,000 Up to $40,000", rate: 2000 },
      { agi: "Over $40,000 Up to $50,000", rate: 1500 },
      { agi: "Over $50,000 Up to $60,000", rate: 1000 },
      { agi: "Over $60,000 Up to $70,000", rate: 500 },
      { agi: "Over $70,000", rate: null },
    ],
  },
  {
    key: "mfj",
    filingLabel: "MFJ or SS",
    rows: [
      { agi: "Up to $40,000", rate: 3000 },
      { agi: "Over $40,000 Up to $60,000", rate: 2500 },
      { agi: "Over $60,000 Up to $80,000", rate: 2000 },
      { agi: "Over $80,000 Up to $100,000", rate: 1500 },
      { agi: "Over $100,000 Up to $120,000", rate: 1000 },
      { agi: "Over $120,000 Up to $140,000", rate: 500 },
      { agi: "Over $140,000", rate: null },
    ],
  },
  {
    key: "hoh",
    filingLabel: "HOH",
    rows: [
      { agi: "Up to $30,000", rate: 3000 },
      { agi: "Over $30,000 Up to $45,000", rate: 2500 },
      { agi: "Over $45,000 Up to $60,000", rate: 2000 },
      { agi: "Over $60,000 Up to $75,000", rate: 1500 },
      { agi: "Over $75,000 Up to $90,000", rate: 1000 },
      { agi: "Over $90,000 Up to $105,000", rate: 500 },
      { agi: "Over $105,000", rate: null },
    ],
  },
  {
    key: "mfs",
    filingLabel: "MFS",
    rows: [
      { agi: "Up to $20,000", rate: 3000 },
      { agi: "Over $20,000 Up to $30,000", rate: 2500 },
      { agi: "Over $30,000 Up to $40,000", rate: 2000 },
      { agi: "Over $40,000 Up to $50,000", rate: 1500 },
      { agi: "Over $50,000 Up to $60,000", rate: 1000 },
      { agi: "Over $60,000 Up to $70,000", rate: 500 },
      { agi: "Over $70,000", rate: null },
    ],
  },
];

function fieldKey(sectionKey, rowIndex) {
  return `nc4_sched2_${sectionKey}_${rowIndex}_children`;
}

/**
 * Schedule 2 — Estimated N.C. Child Deduction Amount (NC-4 page 6).
 */
export default function StateTaxNc4Schedule2({ form, errors, onFormChange }) {
  const skipped = !!form.nc4_schedule2_skip;

  return (
    <div className="state-tax-nc4-w4 state-tax-nc4-sched2">
      <header className="state-tax-nc4-sched2-header">
        <span className="state-tax-nc4-sched2-header-left">Schedule 2</span>
        <span className="state-tax-nc4-sched2-header-center">
          Estimated N.C. Child Deduction Amount
        </span>
      </header>

      <hr className="state-tax-nc4-sched2-hr" />

      <div className="state-tax-nc4-sched2-intro">
        <p>
          A taxpayer who is allowed a federal child tax credit under Internal
          Revenue Code (&quot;IRC&quot;) section 24 is allowed a deduction for
          each qualifying child unless federal adjusted gross income exceeds the
          threshold amount shown below.
        </p>
        <p className="mb-0">
          The N.C. child deduction amount can be claimed only for a child who
          is under 17 years of age on the last day of the year.
        </p>
      </div>

      <div className="state-tax-nc4-sched2-table-wrap">
        <table className="state-tax-nc4-sched2-table inputs_table">
          <thead>
            <tr>
              <th scope="col">Filing status</th>
              <th scope="col">Federal adjusted gross income</th>
              <th scope="col">Number of qualifying children</th>
              <th scope="col">Deduction amount per qualifying child</th>
              <th scope="col">Estimated N.C. child deduction</th>
            </tr>
          </thead>
          <tbody>
            {SCHED2_SECTIONS.flatMap((section) =>
              section.rows.map((row, rowIndex) => {
                const fk = fieldKey(section.key, rowIndex);
                const raw = form[fk] ?? "";
                const count = parseChildCount(raw);
                const estimated =
                  skipped || count === null || row.rate === null
                    ? null
                    : count * row.rate;
                const estimatedDisplay =
                  skipped
                    ? ""
                    : count === null
                      ? ""
                      : row.rate === null
                        ? "—"
                        : fmtMoney(estimated);

                return (
                  <tr key={`${section.key}-${rowIndex}`}>
                    {rowIndex === 0 ? (
                      <th
                        scope="row"
                        rowSpan={section.rows.length}
                        className="state-tax-nc4-sched2-status"
                        title={
                          section.key === "mfj"
                            ? "Married filing jointly or qualifying surviving spouse"
                            : section.key === "hoh"
                              ? "Head of household"
                              : section.key === "mfs"
                                ? "Married filing separately"
                                : undefined
                        }
                      >
                        {section.filingLabel}
                      </th>
                    ) : null}
                    <td className="state-tax-nc4-sched2-td-agi">{row.agi}</td>
                    <td className="state-tax-nc4-sched2-td-children">
                      <div className="state-tax-nc4-sched2-dash-cell">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          className={`state-tax-nc4-sched2-dash-input ${errors[`form.${fk}`] ? "error" : ""}`}
                          name={fk}
                          id={fk}
                          field={`form.${fk}`}
                          value={raw}
                          onChange={onFormChange}
                          disabled={skipped}
                          onWheel={(e) => e.target.blur()}
                          onKeyDown={preventNegativeValueInDocs}
                          aria-label={`Number of qualifying children, ${section.filingLabel}, ${row.agi}`}
                        />
                      </div>
                    </td>
                    <td className="state-tax-nc4-sched2-td-rate state-tax-nc4-sched2-rate">
                      {row.rate === null ? (
                        <span className="state-tax-nc4-sched2-rate-dash">$ –</span>
                      ) : (
                        fmtMoney(row.rate)
                      )}
                    </td>
                    <td className="state-tax-nc4-sched2-td-est">
                      <div className="state-tax-nc4-sched2-dash-cell state-tax-nc4-sched2-dash-cell--money">
                        <input
                          type="text"
                          className="state-tax-nc4-sched2-dash-input state-tax-nc4-sched2-dash-input--readonly"
                          readOnly
                          tabIndex={-1}
                          value={estimatedDisplay}
                          aria-label={`Estimated N.C. child deduction, ${section.filingLabel} ${row.agi}`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>

      <hr className="state-tax-nc4-sched2-hr" />

      <div className="row form-row last state-tax-nc4-sched2-disclaimer mt-2">
        <div className="col">
          <div className="inputs-col">
            <div className="check_radio_group check_disclaimer">
              <div className="check_radio_item">
                <input
                  type="checkbox"
                  name="disclaimer_check"
                  id="disclaimer_check_nc4_p6"
                  field="form.disclaimer_check"
                  checked={!!form.disclaimer_check}
                  onChange={onFormChange}
                  required
                />
                <span className="checkbox_checkbox" />
                <label htmlFor="disclaimer_check_nc4_p6">
                  I have confirmed the above details are true and valid.
                </label>
              </div>
            </div>
            {errors["form.disclaimer_check"] && (
              <span className="error-msg">{errors["form.disclaimer_check"]}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function nc4Sched2ChildrenDefaultFields() {
  const o = {};
  SCHED2_SECTIONS.forEach((section) => {
    section.rows.forEach((_, rowIndex) => {
      o[fieldKey(section.key, rowIndex)] = "";
    });
  });
  return o;
}
